/**
 * Mock utilities - centralized exports
 */

// Type definitions for processListRequest
// Filtering utilities
/**
 * Helper to process a list request with filtering, sorting, and pagination
 *
 * @example
 * ```ts
 * const result = processListRequest(transactions, searchParams, {
 *   searchFields: ['id', 'customer.name', 'customer.email'],
 *   filterConfig: { dateField: 'createdAt', amountField: 'amount' },
 *   sortFields: ['createdAt', 'amount', 'status'],
 *   filterMetadataFields: [
 *     { field: 'status', label: 'Status' },
 *     { field: 'typeCharge', label: 'Payment Type' }
 *   ]
 * })
 * ```
 */
import type { PaginationMeta } from './pagination'
import type { SortOrder } from './sorting'
import type { FilterMetadata } from './filters'
import { applyFilters, parseFilters } from './filters'
import { applySorting } from './sorting'
import { applyPagination } from './pagination'
import { parseListQueryParams, type ValidationResult } from './validation'

export interface ApplyFiltersConfig {
  dateField?: string
  amountField?: string
}

// Re-export FilterMetadata from filters module for type consistency
export type { FilterMetadata } from './filters'

export {
  applyFilters,
  getNestedValue,
  parseFilters,
  type ParsedFilter
} from './filters'

// Sorting utilities
export {
  applySorting,
  applyMultiSorting,
  parseSortParam,
  type SortFieldConfig,
  type SortOptions,
  type SortOrder
} from './sorting'

// Pagination utilities
export {
  applyPagination,
  buildPaginatedResponse,
  calculatePaginationMeta,
  normalizePaginationParams,
  parsePaginationParams,
  ALLOWED_LIMITS,
  PAGINATION_DEFAULTS,
  type AllowedLimit,
  type PaginatedResult,
  type PaginationMeta,
  type PaginationParams
} from './pagination'

// Error utilities
export {
  buildA55ErrorResponse,
  buildErrorResponse,
  createA55ErrorResponse,
  createErrorResponse,
  createNotFoundResponse,
  createValidationErrorResponse,
  handleErrorSimulation,
  parseErrorParam,
  simulateRandomError,
  A55_ERROR_CODES,
  HTTP_ERRORS,
  type A55Error,
  type A55ErrorCode,
  type A55ErrorResponse,
  type ErrorSimulationConfig,
  type HttpErrorCode
} from './errors'

// Delay utilities
export {
  applyDelay,
  applyDemoDelay,
  calculateDelay,
  calculateProgressiveDelay,
  createDelayFn,
  getDelayRange,
  parseDelayParam,
  DELAY_PRESETS,
  type DelayConfig,
  type DelayPreset
} from './delays'

// Trend utilities
export {
  generateTrendSeries,
  type TrendOptions,
  type TrendSeries
} from './trends'

// Validation utilities
export {
  isNonNegativeNumber,
  isPositiveInteger,
  isValidDate,
  isValidId,
  isValidIsoDate,
  isValidUuid,
  invalidResult,
  mergeValidationResults,
  parseListQueryParams,
  validateAmountRange,
  validateDateRange,
  validatePaginationParams,
  validateResourceId,
  validateSortParams,
  validResult,
  type ListQueryParams,
  type ValidationResult
} from './validation'

export interface ProcessListRequestOptions {
  /** Fields to search in */
  searchFields?: string[]
  /** Filter configuration */
  filterConfig?: ApplyFiltersConfig
  /** Allowed sort fields */
  sortFields?: string[]
  /** Default sort field */
  defaultSortBy?: string
  /** Default sort order */
  defaultSortOrder?: SortOrder
  /** Fields for filter metadata */
  filterMetadataFields?: Array<{ field: string, label: string, formatLabel?: (value: string) => string }>
}

export interface ProcessListRequestResult<T> {
  /** Processed items for current page */
  items: T[]
  /** Total count before pagination */
  totalCount: number
  /** Pagination metadata */
  pagination: PaginationMeta
  /** Filter metadata with available options */
  filters: FilterMetadata
  /** Validation result */
  validation: ValidationResult
  /** Meta information */
  meta: {
    requestTime: string
    processingTimeMs: number
  }
}

export function processListRequest<T extends object>(
  data: T[],
  searchParams: URLSearchParams,
  options: ProcessListRequestOptions = {}
): ProcessListRequestResult<T> {
  const startTime = performance.now()

  // Parse and validate query params
  const { params, validation } = parseListQueryParams(searchParams, {
    allowedSortFields: options.sortFields,
    defaultSortBy: options.defaultSortBy,
    defaultSortOrder: options.defaultSortOrder as 'asc' | 'desc'
  })

  // Parse filters from URL
  const parsedFilters = parseFilters(searchParams)

  // Apply filters
  const filtered = applyFilters(data, parsedFilters)

  // Apply sorting
  const sorted = applySorting(filtered, {
    sortBy: params.sortBy,
    sortOrder: params.sortOrder
  })

  // Apply pagination
  const paginated = applyPagination(sorted, {
    page: params.page,
    limit: params.limit
  })

  // Build filter metadata (stubbed for now)
  const filters: FilterMetadata = { available: [], applied: parsedFilters }

  const endTime = performance.now()

  return {
    items: paginated.items,
    totalCount: filtered.length,
    pagination: paginated.pagination,
    filters,
    validation,
    meta: {
      requestTime: new Date().toISOString(),
      processingTimeMs: Math.round((endTime - startTime) * 100) / 100
    }
  }
}
